<template>
  <div class="trip-container">
    <trip-app-bar :trip="trip" />

    <router-view
      :trip="trip"
      :steps="steps"
      :initialized="initialized"
      class="trip-view"
    />

    <v-bottom-navigation
      v-model="view"
      grow
      color="primary"
    >
      <v-btn
        exact
        :to="{ name: 'trip', params: $route.params }"
        replace
      >
        <span>Trip</span>
        <v-icon>mdi-wallet-travel</v-icon>
      </v-btn>

      <v-btn
        exact
        :to="{ name: 'trip-map', params: $route.params }"
        replace
      >
        <span>Map</span>
        <v-icon>mdi-map</v-icon>
      </v-btn>

      <v-btn
        disabled
      >
        <span>Gallery</span>
        <v-icon>mdi-image-multiple</v-icon>

        <v-chip
          color="accent"
          small
          style="position:absolute;opacity:.7;transform:rotate(-10deg)"
        >
          Coming Soon
        </v-chip>
      </v-btn>
    </v-bottom-navigation>

    <v-overlay
      :value="error === 'permission-denied'"
      color="primary"
      opacity="1"
    >
      <div>Sorry, you can't see this trip or it doesn't exist</div>

      <div class="text-center mt-4">
        <v-btn
          color="accent"
          depressed
          :to="{ name: 'home' }"
        >
          Home
        </v-btn>

        <dialog-login
          v-if="!$currentUser"
          title="Sign in to see this trip"
          persistent
        />
      </div>
    </v-overlay>
  </div>
</template>

<script>
import { format, min, max } from 'date-fns'

import DialogLogin from '@/components/DialogLogin'
import TripAppBar from '@/components/TripAppBar'
import Trip from '@/models/Trip'
import TripSteps from '@/models/TripSteps'

export default {
  name: 'Trip',
  components: {
    DialogLogin,
    TripAppBar
  },
  data () {
    return {
      error: null,
      initialized: false,
      steps: new TripSteps(),
      trip: new Trip(),
      view: 0
    }
  },
  computed: {
    tripId () {
      return this.$route.params.id
    }
  },
  async mounted () {
    await this.init()

    this.$store.commit('navigation/setToolbar', {
      image: this.trip.image
        ? this.trip.image : require('@/assets/images/trip-placeholder.jpg'),
      title: this.trip.name
    })
  },
  beforeDestroy () {
    this.$store.commit('navigation/setToolbar')
    this.steps.off('fetch', this.onStepsUpdate)
  },
  methods: {
    async init () {
      try {
        await this.trip.fetch(this.tripId)
        this.generateMetaTags()

        this.steps.on('fetch', this.onStepsUpdate)
        await this.steps.fetch({
          where: [ [ 'tripId', '==', this.tripId ] ],
          orderBy: [
            [ 'startDate', 'asc' ],
            [ 'endDate', 'asc' ]
          ]
        })
      } catch (e) {
        this.error = e.code
      } finally {
        this.initialized = true
      }
    },
    async onStepsUpdate () {
      if (!this.steps.length) { return }

      // calculate start and end of trip
      let allDates = []
      for (const s of this.steps.models) {
        if (s.startDate) { allDates.push(s.startDate) }
        if (s.endDate) { allDates.push(s.endDate) }
      }

      this.trip.startDate = format(min(...allDates), 'YYYY-MM-DD')
      this.trip.endDate = format(max(...allDates), 'YYYY-MM-DD')

      // calculate total trip cost
      this.trip.cost = this.steps.reduce((acc, cur) => acc + (cur.cost || 0), 0)

      if (this.trip.changed()) {
        await this.trip.save()
      }
    },
    generateMetaTags () {
      // <meta name="twitter:card" content="summary" />
      // <meta name="twitter:site" content="@nytimesbits" />
      // <meta name="twitter:creator" content="@nickbilton" />
      // <meta property="og:url" content="http://bits.blogs.nytimes.com/2011/12/08/a-twitter-for-my-sister/" />
      // <meta property="og:title" content="A Twitter for My Sister" />
      // <meta property="og:description" content="In the early days, Twitter grew so quickly that it was almost impossible to add new features because engineers spent their time trying to keep the rocket ship from stalling." />
      // <meta property="og:image" content="http://graphics8.nytimes.com/images/2011/12/08/technology/bits-newtwitter/bits-newtwitter-tmagArticle.jpg" />

      let tag

      tag = document.createElement('meta')
      tag.name = 'twitter:card'
      tag.content = 'summary'
      document.head.appendChild(tag)

      tag = document.createElement('meta')
      tag.name = 'og:title'
      tag.content = this.trip.name
      document.head.appendChild(tag)

      tag = document.createElement('meta')
      tag.name = 'og:url'
      tag.content = `https://app.vjago.com/trips/${this.trip.id}`
      document.head.appendChild(tag)

      if (this.trip.image) {
        tag = document.createElement('meta')
        tag.name = 'og:image'
        tag.content = this.trip.image
        document.head.appendChild(tag)
      }
    }
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.init()
  }
}
</script>

<style lang="scss">
.trip-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.trip-view {
  position: relative;
  overflow: auto;
  flex-grow: 1;
}
</style>
